import type { ICalendarEvent } from '@/features/calendar/interfaces'

import { addDays, areIntervalsOverlapping, endOfWeek, format, isSameDay, parseISO, startOfWeek } from 'date-fns'
import { useMemo } from 'react'

import { ScrollArea } from '@/components/ui/scroll-area'

import { CreateEventDialog } from '@/features/calendar/components/dialogs/add-event-dialog'
import { CalendarTimeline } from '@/features/calendar/components/week-and-day-view/calendar-time-line'
import { EventBlock } from '@/features/calendar/components/week-and-day-view/event-block'
import { WeekSpansRow } from '@/features/calendar/components/week-and-day-view/week-spans-row'

import { calculateMonthTrainingPositions, getTrainingBlockStyle, groupTrainings } from '@/features/calendar/helpers'

import { useCalendar } from '../../contexts/use-calendar'

interface IProps {
  singleDayTrainings: ICalendarEvent[]
  multiDayTrainings: ICalendarEvent[]
}

export function CalendarWeekView({ multiDayTrainings, singleDayTrainings }: IProps) {
  const { selectedDate, canManageEvents } = useCalendar()

  const weekStart = startOfWeek(selectedDate)
  const weekEnd = endOfWeek(weekStart)
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const hours = Array.from({ length: 24 }, (_, i) => i)

  // Calculate positions for multi-day events
  const trainingPositions = useMemo(
    () => calculateMonthTrainingPositions(multiDayTrainings, singleDayTrainings, selectedDate),
    [multiDayTrainings, singleDayTrainings, selectedDate],
  )

  return (
    <>
      <div className="flex flex-col items-center justify-center border-b py-4 text-sm text-t-quaternary sm:hidden">
        <p>Weekly view is not available on smaller devices.</p>
        <p>Please switch to daily or monthly view.</p>
      </div>

      <div className="hidden flex-col sm:flex">
        <div>

          {/* Week header */}
          <div className="relative z-20 flex border-b">
            <div className="w-18"></div>
            <div className="grid flex-1 grid-cols-7 divide-x border-l">
              {weekDays.map((day, index) => (
                <span key={index} className="py-2 text-center text-xs font-medium text-t-quaternary">
                  {format(day, 'EE')}
                  {' '}
                  <span className="ml-1 font-semibold text-t-secondary">{format(day, 'd')}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        <ScrollArea className="h-[736px]" type="always">
          <div className="flex">
            {/* Hours column */}
            <div className="relative w-18">
              {hours.map((hour, index) => (
                <div key={hour} className="relative" style={{ height: '96px' }}>
                  <div className="absolute -top-3 right-2 flex h-6 items-center">
                    {index !== 0 && <span className="text-xs text-t-quaternary">{format(new Date().setHours(hour), 'hh a')}</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Week grid */}
            <div className="relative flex-1 border-l">
              {/* Multi-day spans overlay - positioned within scrollable area */}
              <WeekSpansRow
                weekStart={weekStart}
                weekEnd={weekEnd}
                trainings={multiDayTrainings}
                trainingPositions={trainingPositions}
              />
              
              <div className="grid grid-cols-7 divide-x">
                {weekDays.map((day, dayIndex) => {
                  const dayTrainings = singleDayTrainings.filter(training => isSameDay(parseISO(training.startAt), day) || isSameDay(parseISO(training.endAt), day))
                  const groupedTrainings = groupTrainings(dayTrainings)

                  return (
                    <div key={dayIndex} className="relative">
                      {hours.map((hour, index) => (
                        <div key={hour} className="relative" style={{ height: '96px' }}>
                          {index !== 0 && <div className="pointer-events-none absolute inset-x-0 top-0 border-b"></div>}
                          {canManageEvents ? (
                            <CreateEventDialog startDate={day} startTime={{ hour, minute: 0 }}>
                              <div className="absolute inset-x-0 top-0 h-[48px] cursor-pointer transition-colors hover:bg-bg-primary-hover" />
                            </CreateEventDialog>
                          ) : (
                            <div className="absolute inset-x-0 top-0 h-[48px] pointer-events-none" />
                          )}

                          <div className="pointer-events-none absolute inset-x-0 top-1/2 border-b border-dashed border-b-tertiary"></div>

                          {canManageEvents ? (
                            <CreateEventDialog startDate={day} startTime={{ hour, minute: 30 }}>
                              <div className="absolute inset-x-0 top-[48px] h-[48px] cursor-pointer transition-colors hover:bg-bg-primary-hover" />
                            </CreateEventDialog>
                          ) : (
                            <div className="absolute inset-x-0 top-[48px] h-[48px] pointer-events-none" />
                          )}
                        </div>
                      ))}

                      {groupedTrainings.map((group, groupIndex) =>
                        group.map((training) => {
                          let style = getTrainingBlockStyle(training, day, groupIndex, groupedTrainings.length)
                          const hasOverlap = groupedTrainings.some(
                            (otherGroup, otherIndex) =>
                              otherIndex !== groupIndex
                              && otherGroup.some(otherEvent =>
                                areIntervalsOverlapping(
                                  { start: parseISO(training.startAt), end: parseISO(training.endAt) },
                                  { start: parseISO(otherEvent.startAt), end: parseISO(otherEvent.endAt) },
                                ),
                              ),
                          )

                          if (!hasOverlap)
                            style = { ...style, width: '100%', left: '0%' }

                          return (
                            <div key={training.id} className="absolute p-1" style={style}>
                              <EventBlock training={training} />
                            </div>
                          )
                        }),
                      )}
                    </div>
                  )
                })}
              </div>

              <CalendarTimeline />
            </div>
          </div>
        </ScrollArea>
      </div>
    </>
  )
}
